import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack, Group, Card, Table, Button, Badge, Text, Center, Loader, Alert, Box, ActionIcon,
  Switch, Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { QrCode, Trash2, Smartphone, ShieldCheck } from 'lucide-react';

import { devicesApi, epochsApi, type Device } from '../api';
import { PairingModal } from '../components/PairingModal';
import { numStyle } from '../theme';

function pairedLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

export default function DevicesPage() {
  const qc = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [pairing, setPairing] = useState(false);

  // Endpointy párování potřebují X-Client-Id, které se odvozuje z epoch — než
  // se načtou, nemá smysl se ptát, vrátilo by to 400.
  const epochs = useQuery({ queryKey: ['epochs'], queryFn: epochsApi.getAll });
  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: devicesApi.getAll,
    enabled: epochs.isSuccess,
  });

  const setAdmin = useMutation({
    mutationFn: ({ deviceId, isAdmin }: { deviceId: string; isAdmin: boolean }) =>
      devicesApi.setAdmin(deviceId, isAdmin),
    onSuccess: (_data, vars) => {
      notifications.show({
        message: vars.isAdmin ? 'Zařízení je teď admin' : 'Admin práva odebrána',
        color: 'forest',
      });
      qc.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (e: any) =>
      notifications.show({
        title: 'Změna se nepovedla',
        message: e?.response?.data?.error || e.message,
        color: 'red',
      }),
  });

  const unpair = useMutation({
    mutationFn: devicesApi.unpair,
    onSuccess: () => {
      notifications.show({ message: 'Zařízení odpárováno' });
      qc.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (e: any) =>
      notifications.show({ title: 'Odpárování se nepovedlo', message: e?.response?.data?.error || e.message, color: 'red' }),
  });

  const active = (devices.data ?? []).filter((d) => d.status === 'COMPLETED');

  const confirmUnpair = (d: Device) => {
    if (window.confirm(`Odpárovat ${d.deviceName || d.deviceId}? Mobilní aplikace bude odhlášena.`)) {
      unpair.mutate(d.deviceId);
    }
  };

  const adminSwitch = (d: Device) => (
    <Tooltip label="Admin zařízení smí měnit nastavení hry — třeba stanoviště a interval kola u vysílaček.">
      <Switch
        size="sm"
        checked={d.isAdmin}
        disabled={setAdmin.isPending}
        aria-label={`Admin práva pro ${d.deviceName || d.deviceId}`}
        onChange={(e) => setAdmin.mutate({ deviceId: d.deviceId, isAdmin: e.currentTarget.checked })}
      />
    </Tooltip>
  );

  // Pozor: u dotazu vypnutého přes `enabled` je isLoading false, takže by se prázdný
  // stav vykreslil i tehdy, když se data vůbec nenačetla.
  if (epochs.error) {
    return (
      <Alert color="red" title="Zařízení se nepodařilo načíst">
        Nepovedlo se zjistit organizaci ({(epochs.error as any)?.message}), a bez ní
        server seznam zařízení nevydá.
      </Alert>
    );
  }
  if (devices.error) {
    return <Alert color="red" title="Zařízení se nepodařilo načíst">{(devices.error as any)?.message}</Alert>;
  }
  if (epochs.isLoading || devices.isPending) return <Center h={240}><Loader /></Center>;

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text c="dimmed" fz="sm">
          {active.length === 0 ? 'Zatím není spárované žádné zařízení.' : `Spárováno ${active.length}.`}
        </Text>
        <Button leftSection={<QrCode size={14} />} onClick={() => setPairing(true)}>
          Spárovat zařízení
        </Button>
      </Group>

      <Card padding={0}>
        {active.length === 0 ? (
          <Stack align="center" py="xl" gap={4}>
            <Smartphone size={22} color="var(--mantine-color-dimmed)" />
            <Text fw={600}>Žádné spárované zařízení</Text>
            <Text c="dimmed" fz="sm">Vygeneruj QR kód a naskenuj ho v mobilní aplikaci.</Text>
          </Stack>
        ) : isMobile ? (
          <Box>
            {active.map((d) => (
              <Group key={d.deviceId} p="sm" wrap="nowrap" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6}>
                    <Text fw={650} fz="sm" truncate>{d.deviceName || 'Bez názvu'}</Text>
                    {d.isAdmin && <Badge color="forest" size="xs">admin</Badge>}
                  </Group>
                  <Text c="dimmed" fz="xs" style={numStyle}>Spárováno {pairedLabel(d.pairedAt)}</Text>
                </Box>
                {adminSwitch(d)}
                <ActionIcon color="red" aria-label="Odpárovat" onClick={() => confirmUnpair(d)}>
                  <Trash2 size={14} />
                </ActionIcon>
              </Group>
            ))}
          </Box>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Zařízení</Table.Th>
                  <Table.Th w={90}>Admin</Table.Th>
                  <Table.Th w={170}>Spárováno</Table.Th>
                  <Table.Th w={120} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {active.map((d) => (
                  <Table.Tr key={d.deviceId}>
                    <Table.Td fw={600}>{d.deviceName || 'Bez názvu'}</Table.Td>
                    <Table.Td>{adminSwitch(d)}</Table.Td>
                    <Table.Td><Text fz="sm" c="dimmed" style={numStyle}>{pairedLabel(d.pairedAt)}</Text></Table.Td>
                    <Table.Td>
                      <Group justify="flex-end">
                        <Button size="xs" variant="default" color="red" onClick={() => confirmUnpair(d)}>
                          Odpárovat
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <Group gap={6} align="flex-start" wrap="nowrap">
        <ShieldCheck size={13} style={{ marginTop: 2, flex: '0 0 13px', color: 'var(--mantine-color-dimmed)' }} />
        <Text c="dimmed" fz="xs">
          Admin zařízení smí z terénu měnit nastavení hry. Ostatní zařízení jen zapisují.
          Adminů může být víc než jeden.
          {' '}Poslední kontakt server u zařízení neeviduje, takže tu nejde ukázat, které je právě online.
        </Text>
      </Group>

      <PairingModal
        opened={pairing}
        onClose={() => setPairing(false)}
        onPaired={() => {
          setPairing(false);
          notifications.show({ message: 'Zařízení spárováno', color: 'forest' });
          qc.invalidateQueries({ queryKey: ['devices'] });
        }}
      />
    </Stack>
  );
}
